import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, holdSessionRefresh, SESSION_EXPIRED_EVENT } from "../lib/api";
import { useIdleTimeout } from "../lib/useIdleTimeout";

/** Re-check the server session while a tab stays open overnight. */
export const SESSION_POLL_MS = 60_000;
/** Hide confidential UI after this long away, then confirm the session.
 * Matches the 15-minute access-token TTL so a short tab switch does not flash
 * a spinner, while an overnight return blanks the record before revalidation. */
export const HIDDEN_REVALIDATE_MS = 15 * 60 * 1000;

function sessionEndedReasonFromCode(code?: string | null): SessionEndedReason {
  if (code === "session_replaced") return "replaced";
  if (code === "session_idle") return "idle";
  return "expired";
}

/**
 * Shape of the authenticated session, mirroring LoginResponse from
 * apps/api/app/api/v1/auth.py exactly (field names included) so this
 * provider never needs its own translation layer to drift out of sync.
 */
export interface Session {
  userId: string;
  email: string;
  orgId: string | null;
  orgName: string | null;
  orgSlug: string | null;
  regionCode: string | null;
  isPlatformStaff: boolean;
  isOrgAdmin: boolean;
}

interface LoginResponse {
  user_id: string;
  email: string;
  org_id: string | null;
  org_name: string | null;
  org_slug?: string | null;
  region_code: string | null;
  is_platform_staff: boolean;
  is_org_admin: boolean;
}

/** The org picker option shape returned by the 300 "choose_organization" leg. */
export interface OrgOption {
  org_id: string;
  display_name: string;
}

interface ChooseOrganizationResponse {
  status: "choose_organization";
  organizations: OrgOption[];
}

/**
 * Identity-first login (apps/api/app/api/v1/auth.py): the client posts only
 * email + password. The backend resolves org_id itself from the user's own
 * memberships and returns one of two shapes:
 *   - a normal LoginResponse (200) when there is exactly one membership, or
 *     none (platform staff)
 *   - { status: "choose_organization", organizations: [...] } (300) when the
 *     user belongs to more than one org and must pick one
 * A caller never types or supplies org_id from anywhere except one of the
 * options THIS response returned. A previous live session is replaced
 * automatically; the other device sees session_replaced on its next request.
 */
export type LoginOutcome =
  | { kind: "authenticated" }
  | { kind: "choose_organization"; organizations: OrgOption[] };

export type SessionEndedReason = "expired" | "replaced" | "idle" | null;

interface AuthContextValue {
  session: Session | null;
  status: "loading" | "authenticated" | "anonymous" | "unavailable";
  sessionEndedReason: SessionEndedReason;
  viewLocked: boolean;
  retrySession: () => void;
  login: (email: string, password: string, orgId?: string) => Promise<LoginOutcome>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function toSession(r: LoginResponse): Session {
  return {
    userId: r.user_id,
    email: r.email,
    orgId: r.org_id,
    orgName: r.org_name,
    orgSlug: r.org_slug ?? null,
    regionCode: r.region_code,
    isPlatformStaff: r.is_platform_staff,
    isOrgAdmin: r.is_org_admin,
  };
}

/**
 * Session context. The access/refresh tokens themselves are HttpOnly
 * cookies the browser manages automatically (see lib/api.ts) — this
 * provider never reads or stores a raw token.
 *
 * Restore only from the server's current session. The request wrapper may
 * refresh expired access once; neither the JWT nor session data is cached
 * in browser storage. A late restore must not overwrite a newer login.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [status, setStatus] = useState<AuthContextValue["status"]>("loading");
  const [sessionEndedReason, setSessionEndedReason] = useState<SessionEndedReason>(null);
  const [viewLocked, setViewLocked] = useState(false);
  const authRevision = useRef(0);
  const statusRef = useRef(status);
  const sessionRef = useRef(session);
  statusRef.current = status;
  sessionRef.current = session;
  const [restoreAttempt, setRestoreAttempt] = useState(0);
  const retrySession = useCallback(() => {
    setStatus("loading");
    setRestoreAttempt((value) => value + 1);
  }, []);

  const expireClientSession = useCallback((reason: SessionEndedReason = "expired") => {
    if (statusRef.current === "anonymous") return;
    if (statusRef.current === "loading" && !sessionRef.current) return;
    authRevision.current += 1;
    setSession(null);
    setSessionEndedReason(reason ?? "expired");
    setViewLocked(false);
    setStatus("anonymous");
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const revision = authRevision.current;
    void api.get<LoginResponse>("/auth/session", controller.signal).then((result) => {
      if (controller.signal.aborted || authRevision.current !== revision) return;
      setSession(toSession(result));
      setSessionEndedReason(null);
      setStatus("authenticated");
    }).catch((error: unknown) => {
      if (controller.signal.aborted || authRevision.current !== revision) return;
      setSession(null);
      if (error instanceof ApiError && error.status === 401) {
        setSessionEndedReason(error.code === "session_replaced" ? "replaced" : error.code === "session_idle" ? "idle" : null);
        setStatus("anonymous");
        return;
      }
      setStatus("unavailable");
    });
    return () => controller.abort();
  }, [restoreAttempt]);

  useEffect(() => {
    const onExpired = (event: Event) => {
      const code = event instanceof CustomEvent ? (event.detail as { code?: string | null } | undefined)?.code : null;
      expireClientSession(sessionEndedReasonFromCode(code));
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [expireClientSession]);

  const logoutDueToIdle = useCallback(() => {
    // Revoke on the server when possible, then always clear the client with
    // the idle reason so LoginPage can show the 15-minute message. A failed
    // revoke must not leave confidential UI on screen.
    // Clear the screen first: a slow or hanging revoke must never keep the
    // last page visible.
    expireClientSession("idle");
    void api.post<{ sessions_revoked: string | number }>("/auth/logout").catch(() => {
      // Network/server errors are fine — the client is already torn down.
    });
  }, [expireClientSession]);

  // Stops the session poll (status leaves authenticated) and logs out after
  // 15 minutes with no mouse/keyboard/scroll/touch — docs/03_SECURITY.md §5.1.
  useIdleTimeout(status === "authenticated", logoutDueToIdle);

  useEffect(() => {
    if (status !== "authenticated") return;
    let hiddenAt = document.visibilityState === "hidden" ? Date.now() : 0;
    let inFlight: AbortController | null = null;

    const check = (lockUi: boolean) => {
      if (lockUi) setViewLocked(true);
      inFlight?.abort();
      const controller = new AbortController();
      inFlight = controller;
      const revision = authRevision.current;
      void api.get<LoginResponse>("/auth/session", controller.signal).then((result) => {
        if (controller.signal.aborted || authRevision.current !== revision) return;
        setSession(toSession(result));
        setSessionEndedReason(null);
        setViewLocked(false);
        setStatus("authenticated");
      }).catch((error: unknown) => {
        if (controller.signal.aborted || authRevision.current !== revision) return;
        if (error instanceof ApiError && error.status === 401) {
          expireClientSession(sessionEndedReasonFromCode(error.code));
          return;
        }
        if (lockUi) setViewLocked(false);
      });
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        return;
      }
      const awayMs = hiddenAt ? Date.now() - hiddenAt : 0;
      check(awayMs >= HIDDEN_REVALIDATE_MS);
    };

    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) check(true);
    };

    const interval = window.setInterval(() => check(false), SESSION_POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      inFlight?.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, [status, expireClientSession]);

  const login = useCallback(async (email: string, password: string, orgId?: string): Promise<LoginOutcome> => {
    const releaseRefresh = holdSessionRefresh();
    authRevision.current += 1;
    setSession(null);
    setSessionEndedReason(null);
    setViewLocked(false);
    setStatus("anonymous");
    try {
      // 300 is the multi-org picker.
      const r = await api.postAllowStatus<LoginResponse | ChooseOrganizationResponse>(
        "/auth/login",
        { email, password, org_id: orgId ?? null },
        [300],
      );
      if ("status" in r && r.status === "choose_organization") {
        return { kind: "choose_organization", organizations: r.organizations };
      }
      setSession(toSession(r as LoginResponse));
      setViewLocked(false);
      setStatus("authenticated");
      return { kind: "authenticated" };
    } finally {
      releaseRefresh();
    }
  }, []);

  const logout = useCallback(async () => {
    authRevision.current += 1;
    try {
      const result = await api.post<{ sessions_revoked: string | number }>("/auth/logout");
      if (Number(result.sessions_revoked) !== 1) throw new Error("Session revocation was not confirmed");
    } catch (err) {
      // A rejected/expired session is already unusable. Network/server errors
      // must stay visible rather than pretending server revocation succeeded.
      if (!(err instanceof ApiError) || err.status !== 401) throw err;
    }
    setSession(null);
    setSessionEndedReason(null);
    setViewLocked(false);
    setStatus("anonymous");
  }, []);

  const value = useMemo(
    () => ({ session, status, sessionEndedReason, viewLocked, login, logout, retrySession }),
    [session, status, sessionEndedReason, viewLocked, login, logout, retrySession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth() must be used within an AuthProvider");
  return ctx;
}
